import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Upload, CheckCircle2, ChevronLeft, ChevronRight, ZoomIn, Pencil, Trash2, FilePlus2, Sparkles, FileText, Copy, ChevronDown, Check } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { uploadInChunks } from "@/lib/chunkedUpload";
import { maybeCompressImage } from "@/lib/compressImage";
import { useLanguage } from "@/context/LanguageContext";
import { useProductTypes } from "@/lib/productTypes";
import { useAuth } from "@/context/AuthContext";
import { useQuote } from "@/context/QuoteContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PriceListTable } from "@/components/PriceListTable";
import { PhotoZoomDialog } from "@/components/PhotoZoomDialog";
import { ClassificationFields } from "@/components/product-form/ClassificationFields";
import { RfqPanel } from "@/components/product-form/RfqPanel";
import { OverviewPhotoPicker } from "@/components/product-form/OverviewPhotoPicker";
import { toast } from "@/components/ui/sonner";

const EMPTY_FORM = {
  item_number: "", description: "", description_zh: "", gt_description: "",
  photo_url: "", pattern_photo_url: "", factory_quote_photo_url: "",
  rfq_file_url: "", rfq_file_name: "",
  category: "", subcategory: "", brand: "", buyer: "", seller: "", tag: "", tags: [], factory: "", material: "", selling_feature: "", package: "", moq: "", product_type: "mine", product_types: ["mine"], stock_quantity: "", duty: 0.18, tax_rebate: 0.13, compression_rate: 0, buyer_item_number: "",
  composite_photo_url: null,
};

// 2026-07 · Production Manager edit whitelist. When role === 'production_manager'
// only these fields are editable on existing products (backend enforces the
// same list — this constant just keeps the UI in sync).
const PM_EDITABLE_FIELDS = new Set([
  "photo_url", "pattern_photo_url", "composite_photo_url",
  "description", "description_zh", "gt_description",
  "specification", "specification_zh", "material", "selling_feature",
  "category", "subcategory", "brand",
]);

export default function ProductForm() {
  const { itemNumber } = useParams();
  const isEditing = Boolean(itemNumber);
  const navigate = useNavigate();
  // 2026-02 · When the user opens a product via a scoped list (e.g. TagsPage
  // click → ?tag=xxx or ?mine=1), the ⬅➡ prev/next must stay within that
  // scope instead of walking the whole catalog. We read the scope from the URL
  // query string and forward it both to the /neighbors API and to any onward
  // navigation so it survives the tab.
  const [searchParams] = useSearchParams();
  const scopeTag = searchParams.get("tag") || "";
  const scopeMine = searchParams.get("mine") === "1";
  const scopeQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (scopeTag) p.set("tag", scopeTag);
    if (scopeMine) p.set("mine", "1");
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [scopeTag, scopeMine]);
  // 2026-02-26 · When the user finishes editing, we want the products list to
  // scroll them back to THIS row and highlight it. Save the item number
  // whenever the form mounts / the URL item changes.
  useEffect(() => {
    if (itemNumber) {
      try { sessionStorage.setItem("pdl_scroll_to", itemNumber); } catch {}
    }
  }, [itemNumber]);
  const { t, lang } = useLanguage();
  const productTypes = useProductTypes();
  const typeLabel = (code) => {
    const pt = productTypes.find((x) => x.code === code);
    return (pt && (lang === "en" ? pt.name_en : pt.name_zh)) || code;
  };
  const { user } = useAuth();
  // 2026-02-09 · "加入报价单" button next to Copy — always goes into the
  // active quote picked in the header (no picker on this button, per user
  // spec). If no active quote, we toast a hint instead.
  const { addToQuote } = useQuote();
  const [addingToQuote, setAddingToQuote] = useState(false);
  const [copying, setCopying] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // 外销/采购 can only edit their own products; others are shown read-only.
  // production_manager (2026-07) can edit ANY product but only the whitelist
  // fields — the fieldset stays enabled, per-field disabling is done via
  // ``fieldEditable`` below.
  const role = user?.role;
  const sameName = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  const isPM = role === "production_manager";
  const isSM = role === "sales_manager";
  // 2026-02-09 · Procurement manager mirrors sales_manager but scopes by 采购
  // (buyer) rather than 外销 (seller).
  const isProcMgr = role === "procurement_manager";
  // Sales Manager subordinate names come from JWT-side user dict (populated by
  // backend). Also inject the manager's OWN name so they can edit their own.
  const subordinates = (user?.subordinate_names_list || []).map((n) => (n || "").toLowerCase());
  const isSubordinate = (sellerName) => {
    const s = (sellerName || "").trim().toLowerCase();
    return s && (s === (user?.name || "").trim().toLowerCase() || subordinates.includes(s));
  };
  const canEdit = !isEditing
    || role === "admin"
    || isPM
    || (isSM && isSubordinate(form.seller))
    || (isProcMgr && (isSubordinate(form.buyer) || !(form.buyer || "").trim()))
    || (role === "export" && sameName(form.seller, user?.name))
    || (role === "buyer" && (sameName(form.buyer, user?.name) || !(form.buyer || "").trim()));
  // Field-level gate used by every input. Production Manager only sees the
  // whitelist fields as editable on EXISTING products (create page stays
  // whitelist-only too so what they can enter matches what backend will accept).
  // 2026-02-27 · Buyer / procurement_manager may edit ``buyer_item_number``
  // (采购产品编号) on any product they OWN (buyer name matches) or manage
  // (subordinate for pmgr). All other product-header fields remain locked —
  // backend still refuses non-whitelisted writes for these roles.
  const isPriceListOnly = (role === "buyer" || role === "procurement_manager") && isEditing;
  const BUYER_HEADER_ALLOW = new Set(["buyer_item_number", "product_type", "moq", "factory", "description_zh"]);
  const fieldEditable = (field) => {
    if (!canEdit) return false;
    if (isPM) return PM_EDITABLE_FIELDS.has(field);
    if (isPriceListOnly) {
      // 2026-06 · An unassigned product lets 采购/采购经理 claim it by setting 采购.
      if (field === "buyer") return !loadedBuyer;
      return BUYER_HEADER_ALLOW.has(field);
    }
    return true;
  };
  const [priceLists, setPriceLists] = useState([]);
  const [loadedBuyer, setLoadedBuyer] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [options, setOptions] = useState({ categories: [], subcategories: [], brands: [], buyers: [], sellers: [] });
  const [factories, setFactories] = useState([]);
  const [neighbors, setNeighbors] = useState({ prev: null, next: null });
  // Rename dialog state — Item Number is the primary key referenced by price_lists /
  // price_history so we gate the change behind a confirmation and delegate to the
  // dedicated backend endpoint POST /api/products/{item_number}/rename.
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  // Delete confirmation (2026-02 spec 「产品详细资料里面增加一个删除功能」).
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // 2026-02 · Composite (product photo + 花型) generation state.
  const [compositeBusy, setCompositeBusy] = useState(false);
  // 2026-06 · 图片合成 floating dialog (花型照片 + 合成图片).
  const [compositeOpen, setCompositeOpen] = useState(false);
  // 2026-06 · Header shows "已自动保存 HH:MM:SS" after each successful auto-save.
  const [savedAt, setSavedAt] = useState(null);
  // 2026-07-30 · 询价文件识别 panel — open/closed. Only surfaced on new products.
  const [rfqOpen, setRfqOpen] = useState(false);
  const handleRfqExtracted = useCallback((data) => {
    // Merge AI-extracted fields into the form, preferring non-empty values so
    // a partial re-extraction never wipes a field the user already tweaked.
    setForm((prev) => ({
      ...prev,
      description: data?.extracted?.description || prev.description,
      description_zh: data?.extracted?.description_zh || prev.description_zh,
      specification: data?.extracted?.specification || prev.specification,
      photo_url: data?.product_photo_url || prev.photo_url,
      rfq_file_url: data?.rfq_file_url || prev.rfq_file_url,
      rfq_file_name: data?.rfq_file_name || prev.rfq_file_name,
    }));
    dirtyRef.current = true;
  }, []);

  const handleGenerateComposite = async () => {
    if (!itemNumber || !form.photo_url || !form.pattern_photo_url) return;
    setCompositeBusy(true);
    try {
      const { data } = await api.post(`/products/${encodeURIComponent(itemNumber)}/generate-composite`);
      setForm((f) => ({ ...f, composite_photo_url: data.composite_photo_url }));
      dirtyRef.current = false;  // server already persisted the field
      toast.success(t("products.compositeGenerated"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCompositeBusy(false);
    }
  };

  // 2026-06 · 一键抠图 / 恢复原图 — server-side rembg (same as 产品一览表).
  const [cutoutBusy, setCutoutBusy] = useState(false);
  const handleRemoveBg = async () => {
    if (!itemNumber || !form.photo_url) return;
    setCutoutBusy(true);
    try {
      const { data } = await api.post(`/products/${encodeURIComponent(itemNumber)}/remove-bg`);
      setForm((f) => ({ ...f, photo_url: data.photo_url, original_photo_url: data.original_photo_url }));
      dirtyRef.current = false;
      toast.success(t("products.bgRemoved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCutoutBusy(false);
    }
  };
  const handleRestoreBg = async () => {
    if (!itemNumber) return;
    setCutoutBusy(true);
    try {
      const { data } = await api.post(`/products/${encodeURIComponent(itemNumber)}/restore-bg`);
      setForm((f) => ({ ...f, photo_url: data.photo_url, original_photo_url: null }));
      dirtyRef.current = false;
      toast.success(t("products.originalRestored"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCutoutBusy(false);
    }
  };


  const handleDelete = async () => {
    if (!itemNumber) return;
    setDeleting(true);
    try {
      await api.delete(`/products/${encodeURIComponent(itemNumber)}`);
      toast.success(t("products.deletedToTrash"));
      setDeleteDialogOpen(false);
      dirtyRef.current = false;  // block final auto-save on unmount
      navigate("/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  /** 2026-02-09 · Add this product to the current active quote (via the
   * quote-cart in the header). If there is no active quote, we show a
   * toast telling the user to pick one from the header "选择报价单" menu —
   * per user spec: never open a picker on this button. */
  const handleAddToQuote = async () => {
    if (!itemNumber) return;
    setAddingToQuote(true);
    try {
      const quote = await addToQuote(itemNumber);
      if (!quote) {
        toast.warning(t("products.pickActiveQuoteFirst") || "请先在顶部选择或新建一张活跃报价单");
        return;
      }
      toast.success(`${t("quote.addedTo") || "已加入"} ${quote.quote_number}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddingToQuote(false);
    }
  };

  const handleCopyProduct = async () => {
    if (!itemNumber) return;
    setCopying(true);
    try {
      const { data } = await api.post("/products/copy", { item_numbers: [itemNumber], claim_ownership: true });
      const next = data.copies?.[0]?.new_item_number;
      if (!next) throw new Error("no copy");
      toast.success(`${t("products.copyProduct")} → ${next}`);
      dirtyRef.current = false;
      navigate(`/products/${encodeURIComponent(next)}/edit`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("products.copyFailed"));
    } finally {
      setCopying(false);
    }
  };


  const handleRename = async () => {
    const next = (renameValue || "").trim();
    if (!next || next === itemNumber) {
      setRenameOpen(false);
      return;
    }
    setRenaming(true);
    try {
      const { data } = await api.post(`/products/${encodeURIComponent(itemNumber)}/rename`, {
        new_item_number: next,
      });
      toast.success(t("products.renameSuccess"));
      setRenameOpen(false);
      // Navigate to the new URL — the underlying useEffect refetches everything.
      navigate(`/products/${encodeURIComponent(data.item_number)}/edit`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRenaming(false);
    }
  };

  // ---- auto-save (edit mode): debounce after changes + flush on leave/navigation ----
  const formRef = useRef(form);
  formRef.current = form;
  const dirtyRef = useRef(false);
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;

  const persist = useCallback(async () => {
    if (!isEditing || !canEditRef.current || !dirtyRef.current) return;
    dirtyRef.current = false;
    try {
      await api.put(`/products/${encodeURIComponent(itemNumber)}`, formRef.current);
      setSavedAt(new Date());
    } catch (e) {
      dirtyRef.current = true;
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [isEditing, itemNumber]);

  const persistRef = useRef(persist);
  persistRef.current = persist;

  /** 2026-09 · 压缩率 changed → flush the product save, then reload the price
   *  rows so 压缩后柜量 and the computed prices refresh right away. */
  const refreshPriceRows = useCallback(async () => {
    if (!isEditing) return;
    await persistRef.current();
    try {
      const { data } = await api.get(`/products/${encodeURIComponent(itemNumber)}`);
      setPriceLists(data.price_lists || []);
    } catch { /* keep current rows on failure */ }
  }, [isEditing, itemNumber]);

  // debounced auto-save after edits
  useEffect(() => {
    if (!isEditing || !dirtyRef.current) return;
    const timer = setTimeout(() => persistRef.current(), 1200);
    return () => clearTimeout(timer);
  }, [form, isEditing]);

  // flush pending changes when leaving the form entirely
  useEffect(() => () => { persistRef.current(); }, []);

  useEffect(() => {
    if (!isEditing) return;
    // Precedence:
    //   1. URL ?tag=/?mine= → TagsPage scope → server neighbors with that scope
    //   2. sessionStorage.pdl_scope_list → ProductsList filtered scope (indexed)
    //   3. plain server neighbors (whole catalog)
    if (!scopeTag && !scopeMine) {
      let scopeList = null;
      try {
        const raw = sessionStorage.getItem("pdl_scope_list");
        if (raw) scopeList = JSON.parse(raw);
      } catch { /* ignore */ }
      if (Array.isArray(scopeList) && scopeList.length > 0) {
        const idx = scopeList.indexOf(itemNumber);
        if (idx !== -1) {
          setNeighbors({
            prev: idx > 0 ? scopeList[idx - 1] : null,
            next: idx < scopeList.length - 1 ? scopeList[idx + 1] : null,
          });
          return;
        }
      }
    }
    // Server-side neighbors, honouring any tag/mine query scope.
    const params = {};
    if (scopeTag) params.tag = scopeTag;
    if (scopeMine) params.mine = "1";
    api.get(`/products/${encodeURIComponent(itemNumber)}/neighbors`, { params })
      .then(({ data }) => setNeighbors(data))
      .catch(() => setNeighbors({ prev: null, next: null }));
  }, [isEditing, itemNumber, scopeTag, scopeMine]);

  const goNeighbor = async (target) => {
    if (!target) return;
    await persist();
    navigate(`/products/${encodeURIComponent(target)}/edit${scopeQuery}`);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products/meta/options");
        setOptions(data);
      } catch (error) {
        console.warn("Failed to load select options (non-blocking):", error);
      }
      try {
        const { data: facs } = await api.get("/factories");
        setFactories(facs.map((f) => f.name));
      } catch (error) {
        // 外销 has no access to the factories module — 403 is expected; dropdown stays empty
        if (error?.response?.status !== 403) console.warn("Failed to load factories:", error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    (async () => {
      try {
        const { data } = await api.get(`/products/${encodeURIComponent(itemNumber)}`);
        dirtyRef.current = false;
        setForm({
          ...data,
          // 2026-06 · Legacy products only have the single product_type.
          product_types: (data.product_types || []).length
            ? data.product_types
            : (data.product_type ? [data.product_type] : []),
        });
        setLoadedBuyer((data.buyer || "").trim());
        setPriceLists(data.price_lists || []);
        // 2026-02 · Fire-and-forget: record this view so the product appears
        // in the caller's 最近查看 tab. Failure is silent — this is only
        // best-effort telemetry.
        api.post(`/products/${encodeURIComponent(itemNumber)}/view`).catch(() => {});
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, [isEditing, itemNumber]);

  // New product: pre-fill 外销/采购 with the logged-in user's name based on their role
  useEffect(() => {
    if (isEditing || !user?.name) return;
    if (user.role === "export") setForm((f) => ({ ...f, seller: f.seller || user.name }));
    if (user.role === "buyer" || user.role === "procurement_manager") setForm((f) => ({ ...f, buyer: f.buyer || user.name }));
  }, [isEditing, user]);

  const setField = (field, value) => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      // 2026-07-29 · Client-side resize + JPEG re-encode before chunking
      // shrinks a typical 5 MB phone photo to ~500 KB. Massive win on slow
      // uplinks (mainland China → Cloudflare R2 path).
      const compressed = await maybeCompressImage(file);
      const uploadId = await uploadInChunks(compressed);
      const formData = new FormData();
      formData.append("upload_id", uploadId);
      formData.append("filename", compressed.name);
      const { data } = await api.post("/products/upload-image", formData);
      setField("photo_url", data.url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPhotoUploading(false);
    }
  };

  // Pattern photo upload (2026-02 · 花型照片). Reuses the same chunked upload
  // pipeline as the primary photo, just writes to a different field.
  const [patternPhotoUploading, setPatternPhotoUploading] = useState(false);
  const handlePatternPhotoUpload = async (file) => {
    if (!file) return;
    setPatternPhotoUploading(true);
    try {
      const compressed = await maybeCompressImage(file);
      const uploadId = await uploadInChunks(compressed);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("filename", compressed.name);
      const { data } = await api.post("/products/upload-image", fd);
      setField("pattern_photo_url", data.url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPatternPhotoUploading(false);
    }
  };

  const handleTranslate = async () => {
    if (!form.description) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/ai/translate", {
        description: form.description,
        specification: "",
      });
      setForm((f) => ({
        ...f,
        description_zh: data.description_zh || f.description_zh,
      }));
      dirtyRef.current = true;
      toast.success(t("ai.success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTranslating(false);
    }
  };

  // NOTE: 2026-02 — AI OCR extraction UI (工厂报价单照片) removed from the
  // product edit page per user spec. Backend `/api/ai/extract-factory-quote`
  // still exists; re-wire a new caller here if OCR ever returns to this page.

  const handleSave = async () => {
    if (!form.item_number) {
      toast.error(t("products.itemNumber"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/products", form);
      for (const row of priceLists) {
        if (!row.id) {
          await api.post("/price-lists", { ...row, item_number: data.item_number });
        }
      }
      toast.success(t("common.save"));
      navigate(`/products/${encodeURIComponent(data.item_number)}/edit`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // 2026-06 · 返回按钮的文案：从工厂的产品清单进来时显示工厂名。
  const backLabel = (() => {
    try { return sessionStorage.getItem("pdl_return_label") || ""; } catch { return ""; }
  })();

  return (
    <div className="animate-fade-in-up">
      <button
        onClick={() => {
          // 2026-02 · Prefer the exact list URL we came from — after N
          // 上一个/下一个 clicks, `navigate(-1)` would only walk back one
          // step. `pdl_return_url` captured the full list URL (with filters)
          // when ProductsList mounted so we can hop straight back to it.
          let target = "";
          try { target = sessionStorage.getItem("pdl_return_url") || ""; } catch {}
          // Also remember the current item so ProductsList can scroll+highlight
          // the row we were editing.
          try { if (itemNumber) sessionStorage.setItem("pdl_scroll_to", itemNumber); } catch {}
          if (target) navigate(target);
          else navigate(-1);
        }}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        data-testid="back-to-products-button"
      >
        <ArrowLeft size={14} /> {backLabel || t("products.title")}
      </button>
      <div className={`flex flex-col ${rfqOpen ? "lg:flex-row lg:items-start lg:gap-6" : ""}`}>
        <div className={rfqOpen ? "flex-1 min-w-0" : "max-w-6xl"}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-base font-bold tracking-tight">
            {isEditing ? t("products.editTitle") : t("products.newTitle")}
          </h1>
          {/* 2026-07-30 · 询价文件 toggle. Only shown when creating a NEW product
              (per user spec — RFQ extraction is an intake step, not an edit
              action). Clicking flips the right-side panel on/off. */}
          {!isEditing && (
            <button
              type="button"
              onClick={() => setRfqOpen((v) => !v)}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium transition-colors ${
                rfqOpen
                  ? "bg-brand text-white border-brand"
                  : "bg-card border-border hover:bg-accent"
              }`}
              data-testid="rfq-toggle-btn"
              title={t("rfq.toggleHint") || "上传客户询价文件，AI 自动识别并填入左边表单"}
            >
              <FileText size={14} />
              {t("rfq.toggleLabel") || "询价文件"}
            </button>
          )}
        </div>
        {isEditing && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1 hidden md:flex items-center gap-1" data-testid="product-autosave-indicator">
              {savedAt ? (
                <>
                  <CheckCircle2 size={13} className="text-emerald-600" />
                  {t("products.autoSaved")} {savedAt.toLocaleTimeString(lang === "zh" ? "zh-CN" : "en-US", { hour12: false })}
                </>
              ) : t("products.autoSaveHint")}
            </span>
            <Button variant="outline" size="sm" className="rounded-md h-9" disabled={!neighbors.prev} onClick={() => goNeighbor(neighbors.prev)} data-testid="product-prev-button">
              <ChevronLeft size={15} /> {t("products.prevProduct")}
            </Button>
            <Button variant="outline" size="sm" className="rounded-md h-9" disabled={!neighbors.next} onClick={() => goNeighbor(neighbors.next)} data-testid="product-next-button">
              {t("products.nextProduct")} <ChevronRight size={15} />
            </Button>
            {/* 2026-06 · 复制这条记录 — duplicates the product (incl. price list)
                and attributes 采购 + 外销 to the logged-in user (user spec). */}
            {["admin", "export"].includes(role) && isEditing && (
              <Button
                variant="outline" size="sm"
                className="rounded-md h-9"
                onClick={handleCopyProduct}
                disabled={copying}
                data-testid="product-copy-button"
              >
                <Copy size={15} /> {copying ? "…" : t("products.copyProduct")}
              </Button>
            )}
            {/* 2026-02-09 · 加入报价单 · admin / export / gt_export /
                sales_manager can push this product into their active quote
                (cart icon in the header). Buyer/customer/factory: N/A. */}
            {["admin", "export", "gt_export", "sales_manager"].includes(role) && (
              <Button
                variant="outline" size="sm"
                className="rounded-md h-9"
                onClick={handleAddToQuote}
                disabled={addingToQuote}
                data-testid="product-add-to-quote-button"
              >
                <FilePlus2 size={15} /> {addingToQuote ? "…" : (t("products.addToQuote") || "加入报价单")}
              </Button>
            )}
            {/* Delete button — per user spec 2026-02「只有外销可以删除，只可以
                删除外销属于自己的产品」. Admin also allowed as a safety net.
                Backend re-checks ownership on DELETE /api/products/{item}. */}
            {(role === "admin" || isPM || (role === "export" && sameName(form.seller, user?.name))) && (
              <Button
                variant="outline" size="sm"
                className="rounded-md h-9 text-rose-600 border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="product-delete-button"
              >
                <Trash2 size={15} /> {t("common.delete")}
              </Button>
            )}
          </div>
        )}
      </div>

      {!canEdit && (
        <div className="mb-4 px-4 py-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm" data-testid="product-readonly-banner">
          {t("products.readOnlyNotOwner")}
        </div>
      )}

      {/* 2026-02-09 · Two fieldsets:
       *   1. Product-header fields (basic info / images / description / etc.):
       *      disabled when the user is buyer/procurement_manager (they can
       *      only touch the price list) OR when the product isn't theirs.
       *   2. Classification section below the price list: same rule as (1).
       * The PriceListTable in between sits OUTSIDE any disabled fieldset so
       * price rows stay editable for buyers on their own / subordinate items.
       */}
      <fieldset disabled={!canEdit} className="min-w-0 border-0 p-0 m-0">
      {/* 2026-06 · Compact two-column layout (user mockup):
       *   left  → 基本信息 + 产品图片
       *   right → 描述与规格 + 分类与工厂
       *   below → 产品报价列表 (full width) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3 items-stretch">
      <div className="flex flex-col gap-3 min-w-0">
      <div className="bg-card border border-border rounded-lg shadow-sm p-3">
        {/* ── Section: 基本信息 ─────────────────────────────────────────── */}
        <section aria-labelledby="pf-identity">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="h-4 w-0.5 bg-brand rounded-full" aria-hidden />
            <h2 id="pf-identity" className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t("products.sectionIdentity")}
            </h2>
            {/* 2026-06 · 建档日期挪到「基本信息」标题旁：FM 同步来的显示 FM 建档
                日期，App 里新建的显示 App 创建日期。 */}
            {isEditing && (form.fm_creation_date || form.created_at) && (
              <span className="text-[11px] text-muted-foreground font-mono" data-testid="product-creation-date">
                {form.fm_creation_date
                  ? `${t("products.fmCreationDate")}: ${form.fm_creation_date}`
                  : `${t("products.appCreationDate")}: ${String(form.created_at).slice(0, 10)}`}
              </span>
            )}
          </div>
        {/* Row 1 — identity (2026-06 mockup): 3 columns
            [货号] | [采购产品编号] | [品牌]  ·  then [产品类型] | [库存数量] */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-2">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 h-5">
              <div className="flex items-center gap-1.5 min-w-0">
                <Label className="whitespace-nowrap">{t("products.itemNumber")}</Label>
                {/* 重命名 button now sits inline with the ITEM NUMBER label
                    (2026-02 v3 spec 「重命名放在 item 字段标题旁边」). Icon-only
                    to guarantee it fits even in the narrowest column widths. */}
                {isEditing && canEdit && !isPriceListOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setRenameValue(form.item_number); setRenameOpen(true); }}
                    data-testid="product-rename-button"
                    className="rounded-md h-6 w-6 p-0 shrink-0 text-brand hover:bg-brand/10"
                    title={t("products.rename")}
                  >
                    <Pencil size={13} />
                  </Button>
                )}
              </div>
              {!isEditing && (
                <span className="text-[11px] text-muted-foreground shrink-0">{(form.item_number || "").length}/15</span>
              )}
            </div>
            <Input
              value={form.item_number}
              disabled={isEditing}
              maxLength={15}
              onChange={(e) => setField("item_number", e.target.value.slice(0, 15))}
              data-testid="product-item-number-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="h-5 flex items-center">{t("products.buyerItemNumber")}</Label>
            <Input
              value={form.buyer_item_number || ""}
              onChange={(e) => setField("buyer_item_number", e.target.value)}
              data-testid="product-buyer-item-number-input"
              disabled={!fieldEditable("buyer_item_number")}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="h-5 flex items-center">{t("products.brand")}</Label>
            <Input
              list="brand-options"
              value={form.brand || ""}
              placeholder={t("products.typeOrSelect")}
              onChange={(e) => setField("brand", e.target.value)}
              data-testid="product-brand-input"
              disabled={!fieldEditable("brand")}
            />
            <datalist id="brand-options">
              {(options.brands || []).map((o) => <option key={o} value={o} />)}
            </datalist>
          </div>
          <div className="space-y-1.5">
            <Label className="h-5 flex items-center">{t("products.productType")}</Label>
            {/* 2026-06 · 产品类型 is MULTI-select (user spec). The primary type
                (first pick) still lives in form.product_type for FM sync. */}
            <Popover>
              <PopoverTrigger asChild disabled={!fieldEditable("product_type")}>
                <Button
                  type="button" variant="outline"
                  className="w-full justify-between font-normal h-9 px-3"
                  data-testid="product-type-select"
                  disabled={!fieldEditable("product_type")}
                >
                  <span className="truncate text-sm">
                    {(form.product_types || []).length
                      ? (form.product_types || []).map((c) => typeLabel(c)).join(" · ")
                      : <span className="text-muted-foreground">{t("products.select")}</span>}
                  </span>
                  <ChevronDown size={14} className="opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-1.5" align="start">
                {productTypes.map((pt) => {
                  const picked = (form.product_types || []).includes(pt.code);
                  return (
                    <button
                      key={pt.code} type="button"
                      onClick={() => {
                        const cur = form.product_types || [];
                        setField("product_types", picked ? cur.filter((c) => c !== pt.code) : [...cur, pt.code]);
                      }}
                      data-testid={`product-type-option-${pt.code}`}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-secondary transition-colors text-left"
                    >
                      <span className={`h-3.5 w-3.5 shrink-0 rounded border flex items-center justify-center ${picked ? "bg-brand border-brand text-white" : "border-border"}`}>
                        {picked && <Check size={10} />}
                      </span>
                      {(lang === "en" ? pt.name_en : pt.name_zh) || pt.code}
                    </button>
                  );
                })}
                {(form.product_types || []).length > 0 && (
                  <button type="button" onClick={() => setField("product_types", [])}
                    data-testid="product-type-clear"
                    className="w-full mt-1 px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-secondary transition-colors text-left">
                    {t("common.clear")}
                  </button>
                )}
              </PopoverContent>
            </Popover>
          </div>
          {/* 库存数量 — 2026-02 spec 「产品类型加库存数量字段」. Free-text so the
              buyer can enter units alongside the number (e.g. "1200 pcs").
              2026-06 · Spans the remaining 2 columns so its right edge lines up
              with 品牌 above (user visual edit). */}
          <div className="space-y-1.5 md:col-span-2">
            <Label className="h-5 flex items-center">{t("products.stockQuantity")}</Label>
            <Input
              value={form.stock_quantity || ""}
              onChange={(e) => {
                const v = e.target.value;
                dirtyRef.current = true;
                // 2026-02 · Auto-flip product_type → "stock" (库存产品) as soon
                // as the user enters a numeric stock quantity. Only fires when
                // the current type is empty or already "stock" so we never
                // clobber a deliberate manual choice (标准/新/样本).
                setForm((prev) => {
                  const patch = { ...prev, stock_quantity: v };
                  const hasNumber = /\d/.test(v || "");
                  const types = prev.product_types || [];
                  if (hasNumber && (types.length === 0 || types.includes("stock"))) {
                    patch.product_types = types.includes("stock") ? types : [...types, "stock"];
                  }
                  return patch;
                });
              }}
              placeholder="e.g. 1200"
              data-testid="product-stock-quantity-input"
              disabled={!fieldEditable("stock_quantity")}
            />
          </div>
          {/* 2026-06 · 标签 field removed from this form per user spec —
              tagging is done from the products list / 标签管理 page. */}
        </div>
        </section>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-3 flex-1">
        {/* ── Section: 图片 ─────────────────────────────────────────────── */}
        {/* 2026-06 · "PHOTOS" section title and the "PRODUCT PHOTO" label were
            removed per user mockup — the thumbnail speaks for itself. */}
        <section aria-label={t("products.sectionMedia")} className="h-full flex flex-col">
        {/* 2026-06 · The photo fills the whole card (its height is matched to
            分类与工厂 on the right); buttons sit in a fixed column beside it. */}
        <div className="flex items-stretch gap-3 flex-1 min-h-[220px]">
          {form.photo_url ? (
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              className="relative group flex-1 min-w-0 rounded-md overflow-hidden"
              data-testid="product-photo-zoom-trigger"
              title={t("products.zoomPhoto")}
            >
              <img
                src={`${API.replace("/api", "")}${form.photo_url}`}
                alt="product"
                data-testid="product-photo-preview"
                className="w-full h-full object-contain cursor-zoom-in"
              />
              <span className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/60 text-white text-[10px] font-mono opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <ZoomIn size={11} /> {t("products.zoomPhoto")}
              </span>
            </button>
          ) : (
            <div className="flex-1 min-w-0 rounded-md border border-dashed border-border bg-secondary/30" />
          )}
          <div className="flex flex-col gap-2 shrink-0">
            <label className="cursor-pointer">
              <input type="file" accept="image/*" className="hidden" data-testid="product-photo-input" onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
              <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-secondary transition-colors">
                <Upload size={14} /> {photoUploading ? "..." : t("products.uploadPhoto")}
              </span>
            </label>
            {/* 花型照片 + 合成图片 live in the 图片编辑 dialog. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-md h-9"
              onClick={() => setCompositeOpen(true)}
              data-testid="product-composite-open"
            >
              <Sparkles size={14} /> {t("products.photoEdit")}
            </Button>
          </div>
        </div>
        </section>
      </div>
      </div>

      <div className="space-y-3 min-w-0">
      <div className="bg-card border border-border rounded-lg shadow-sm p-3">
        {/* ── Section: 描述与规格 ─────────────────────────────────────────── */}
        <section aria-labelledby="pf-desc">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="h-4 w-0.5 bg-brand rounded-full" aria-hidden />
            <h2 id="pf-desc" className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t("products.sectionDescription")}
            </h2>
          </div>
        {/* Row 3 — descriptions (2026-02 v2 spec):
            LEFT  : 产品描述（英文）textarea
            RIGHT : 中文描述 + 自动翻译按钮（moved here from bottom）
                    + Conditional field below:
                      · admin / gt_export → GT 产品描述
                      · other export / buyer → PACKAGE 包装 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("products.description")}</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                data-testid="product-description-input"
                className="resize-y min-h-[64px]"
                disabled={!fieldEditable("description")}
              />
            </div>
            {/* 2026-06 · 产品描述（中文）restored per user spec, with the
                自动翻译成中文 helper next to its label. */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>{t("products.descriptionZh")}</Label>
                {fieldEditable("description_zh") && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px] text-brand hover:bg-brand/10"
                    onClick={handleTranslate}
                    disabled={translating || !form.description}
                    data-testid="product-translate-button"
                  >
                    <Sparkles size={12} /> {t("products.autoTranslate")}
                  </Button>
                )}
              </div>
              <Textarea
                rows={2}
                value={form.description_zh || ""}
                onChange={(e) => setField("description_zh", e.target.value)}
                data-testid="product-description-zh-input"
                className="resize-y min-h-[64px]"
                disabled={!fieldEditable("description_zh")}
              />
            </div>
            {/* MOQ (wider) + 关税 (half width) — 2026-06 mockup. */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>{t("products.moq")}</Label>
                <Input
                  value={form.moq || ""}
                  onChange={(e) => setField("moq", e.target.value)}
                  data-testid="product-moq-input"
                  disabled={!fieldEditable("moq")}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("products.duty")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.duty ?? 0.1}
                  onChange={(e) => setField("duty", parseFloat(e.target.value) || 0)}
                  data-testid="product-duty-input"
                  disabled={!fieldEditable("duty")}
                />
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("products.material")}</Label>
              <Textarea
                rows={3}
                value={form.material || ""}
                onChange={(e) => setField("material", e.target.value)}
                data-testid="product-material-input"
                className="resize-y min-h-[64px]"
                disabled={!fieldEditable("material")}
              />
            </div>
            {/* Role-gated slot: admin / gt_export see GT 产品描述; other export
                & buyer see the PACKAGE 包装 field instead (2026-02 v2 spec). */}
            {(user?.role === "admin" || user?.role === "gt_export") ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>{t("products.gtDescription")}</Label>
                  <span className="text-[11px] text-muted-foreground">{(form.gt_description || "").length}/30</span>
                </div>
                <input
                  type="text"
                  maxLength={30}
                  value={form.gt_description || ""}
                  onChange={(e) => setField("gt_description", e.target.value.slice(0, 30))}
                  onFocus={() => {
                    if (!form.gt_description && form.description) {
                      setField("gt_description", (form.description || "").slice(0, 30));
                    }
                  }}
                  placeholder={(form.description || "").slice(0, 30) || t("products.gtDescriptionHint")}
                  disabled={!fieldEditable("gt_description")}
                  data-testid="product-gt-description-input"
                  className="w-full h-9 px-3 rounded-md border border-input bg-transparent text-sm focus:border-brand focus:outline-none"
                />
                <p className="text-[11px] text-muted-foreground">{t("products.gtDescriptionHint")}</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>{t("products.package")}</Label>
                <Input
                  value={form.package || ""}
                  onChange={(e) => setField("package", e.target.value)}
                  data-testid="product-package-input"
                  disabled={!fieldEditable("package")}
                />
              </div>
            )}
          </div>
        </div>
        </section>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm p-3 space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="h-4 w-0.5 bg-brand rounded-full" aria-hidden />
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {t("products.sectionClassification")}
          </h2>
        </div>
        <ClassificationFields form={form} setField={setField} options={options} factories={factories} onTranslate={handleTranslate} translating={translating} fieldEditable={fieldEditable} />

        {/* DescriptionFields removed 2026-02 v2 · 中文描述 relocated to Row 3
            right column alongside the auto-translate button. Bottom section
            now only contains classification metadata + timestamps. */}

      </div>
      </div>
      </div>

      </fieldset>

      {/* 产品报价列表 — full width below the two columns */}
      <div className="bg-card border border-border rounded-lg shadow-sm p-3 mb-3">
        {/* 2026-06 · Cap the price list height so the page fits one laptop
            screen; the table scrolls internally when there are many sizes. */}
        <div className="max-h-[40vh] overflow-y-auto">
          <PriceListTable itemNumber={isEditing ? itemNumber : null} rows={priceLists} setRows={setPriceLists} buyerItemNumber={(form.buyer_item_number || "").trim()} description={form.description || ""}
            compression={{ value: form.compression_rate ?? 0,
                           onChange: (v) => setField("compression_rate", v),
                           onCommit: refreshPriceRows,
                           disabled: !canEdit || !fieldEditable("compression_rate") }} />
        </div>
      </div>
      {/* 2026-06 · 卖点 moved BELOW the price list per user spec. */}
      <fieldset disabled={!canEdit} className="min-w-0 border-0 p-0 m-0">
        <div className="bg-card border border-border rounded-lg shadow-sm p-3 mb-3">
          <div className="space-y-1.5">
            <Label htmlFor="pf-selling-feature">{t("products.sellingFeature")}</Label>
            <Textarea
              id="pf-selling-feature"
              rows={2}
              value={form.selling_feature || ""}
              onChange={(e) => setField("selling_feature", e.target.value)}
              data-testid="product-selling-feature-input"
              className="resize-y min-h-[56px]"
              placeholder={t("products.sellingFeatureHint")}
              disabled={!fieldEditable("selling_feature")}
            />
          </div>
        </div>
      </fieldset>

      {canEdit && !isEditing && (
        <Button onClick={handleSave} disabled={saving} data-testid="save-product-button" className="rounded-md bg-brand hover:bg-brand-hover transition-colors px-8">
          {saving ? "..." : t("common.save")}
        </Button>
      )}
      <PhotoZoomDialog
        open={zoomOpen}
        onOpenChange={setZoomOpen}
        src={form.photo_url ? `${API.replace("/api", "")}${form.photo_url}` : ""}
        alt={form.item_number}
      />
      {/* 2026-06 · 图片合成 dialog — 花型照片 upload + 合成图片 generation. */}
      <Dialog open={compositeOpen} onOpenChange={setCompositeOpen}>
        <DialogContent className="max-w-[1150px]" data-testid="product-composite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Sparkles size={16} /> {t("products.photoEdit")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-3 md:col-span-2">
              {/* 2026-06 · 产品图片 + 产品一览表 picker (same as the quote item
                  editor) so the main photo can be swapped right here. */}
              <div className="space-y-1.5">
                <Label>{t("products.photo")}</Label>
                <div className="flex items-start gap-2">
                  {form.photo_url ? (
                    <div className="w-36 h-36 rounded-md overflow-hidden bg-white border border-border shrink-0">
                      <img
                        src={`${API.replace("/api", "")}${form.photo_url}`}
                        alt={form.item_number}
                        data-testid="composite-product-photo-preview"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-36 h-36 rounded-md border border-dashed border-border bg-secondary/40 shrink-0" />
                  )}
                  <OverviewPhotoPicker
                    itemNumber={form.item_number}
                    value={form.photo_url}
                    disabled={!fieldEditable("photo_url")}
                    onPick={(url) => setField("photo_url", url)}
                  />
                </div>
                {/* 2026-06 · 一键抠图 — same rembg pipeline as 产品一览表. */}
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-md h-8"
                    disabled={!canEdit || !form.photo_url || cutoutBusy}
                    onClick={handleRemoveBg}
                    data-testid="product-remove-bg"
                  >
                    <Sparkles size={13} /> {cutoutBusy ? t("products.bgRemoving") : t("products.removeBg")}
                  </Button>
                  {form.original_photo_url && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="rounded-md h-8 text-xs text-muted-foreground"
                      disabled={!canEdit || cutoutBusy}
                      onClick={handleRestoreBg}
                      data-testid="product-restore-bg"
                    >
                      {t("products.restoreOriginal")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.patternPhoto")}</Label>
              <div className="flex flex-col items-start gap-2 w-fit">
                {form.pattern_photo_url && (
                  <div className="relative w-48 h-48 rounded-md overflow-hidden bg-white border border-border"
                    data-testid="product-pattern-photo-preview-wrap">
                    <img
                      src={`${API.replace("/api", "")}${form.pattern_photo_url}`}
                      alt="pattern"
                      data-testid="product-pattern-photo-preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <label className={`cursor-pointer ${canEdit ? "" : "pointer-events-none opacity-50"}`}>
                    <input type="file" accept="image/*" className="hidden" data-testid="product-pattern-photo-input"
                      onChange={(e) => handlePatternPhotoUpload(e.target.files?.[0])} />
                    <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-secondary transition-colors">
                      <Upload size={14} /> {patternPhotoUploading ? "..." : t("products.uploadPatternPhoto")}
                    </span>
                  </label>
                  {form.pattern_photo_url && (
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => setField("pattern_photo_url", "")}
                      data-testid="product-pattern-photo-clear"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                    >
                      {t("common.remove")}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("products.compositePhoto")}</Label>
              <div className="flex flex-col items-start gap-2 w-fit">
                {form.composite_photo_url ? (
                  <div className="relative w-64 h-64 rounded-md overflow-hidden bg-white border border-border" data-testid="product-composite-photo-preview-wrap">
                    <img
                      src={`${API.replace("/api", "")}${form.composite_photo_url}`}
                      alt="composite"
                      data-testid="product-composite-photo-preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-64 h-64 rounded-md border border-dashed border-border bg-secondary/40 flex items-center justify-center text-xs text-muted-foreground text-center px-3">
                    {t("products.compositeEmpty")}
                  </div>
                )}
                <button
                  type="button"
                  disabled={!canEdit || !form.photo_url || !form.pattern_photo_url || compositeBusy}
                  onClick={handleGenerateComposite}
                  data-testid="product-generate-composite"
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-brand hover:bg-brand-hover text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t("products.generateComposite")}
                >
                  <Sparkles size={14} /> {compositeBusy ? "..." : t("products.generateComposite")}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompositeOpen(false)} className="rounded-md">
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-md" data-testid="product-rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Pencil size={16} /> {t("products.renameTitle")}
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              {t("products.renameWarning")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-secondary/40 px-3 py-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">{t("products.renameFrom")}</p>
              <p className="font-mono font-semibold text-sm" data-testid="product-rename-current">{itemNumber}</p>
            </div>
            <div>
              <Label className="text-xs">{t("products.renameTo")}</Label>
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value.slice(0, 15))}
                maxLength={15}
                autoFocus
                data-testid="product-rename-input"
                className="mt-1 font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)} disabled={renaming} className="rounded-md">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleRename}
              disabled={renaming || !renameValue.trim() || renameValue.trim() === itemNumber}
              data-testid="product-rename-confirm-button"
              className="rounded-md bg-brand hover:bg-brand-hover"
            >
              {renaming ? "..." : t("products.renameConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog (2026-02) — soft-deletes to 回收站; auto-purged in 30 days */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md" data-testid="product-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center gap-2 text-rose-700">
              <Trash2 size={16} /> {t("products.deleteTitle")}
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              {t("products.deleteWarning").replace("{item}", itemNumber || "")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} className="rounded-md">
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              data-testid="product-delete-confirm-button"
              className="rounded-md bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? "..." : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </div>
        {/* 2026-07-30 · Right-side RFQ panel. Rendered inside the flex row so
            it sits next to the form column on ≥ lg screens; on mobile it
            stacks below the form. */}
        {rfqOpen && !isEditing && (
          <RfqPanel onExtracted={handleRfqExtracted} />
        )}
      </div>
    </div>
  );
}
